import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { DocumenteBruteEa } from '../../components/settings/DocumenteBruteEa'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { PasswordField } from '../../components/ui/PasswordField'

interface EaSettings {
  apiBaseUrl: string | null
  tokensSet: boolean
  getDataTokensSet: boolean
  newPaymentsBaseUrl: string | null
  newPaymentsTokensSet: boolean
  newGlBaseUrl: string | null
  newGlTokensSet: boolean
  newPartnerBaseUrl: string | null
  newPartnerTokensSet: boolean
  docOutBaseUrl: string | null
  docOutTokensSet: boolean
  docInBaseUrl: string | null
  docInTokensSet: boolean
  invDocsTokensSet: boolean
  glExportTokensSet?: boolean
  enabled: boolean
  lastSyncAt: string | null
  lastSyncCount: number | null
  updatedAt: string
}

interface SettingsResp {
  settings: EaSettings | null
}

interface RezultatTest {
  nume: string
  foloseste: string
  stare: 'ok' | 'neconfigurat' | 'eroare' | 'raspuns'
  detaliu: string
}

/**
 * URL-ul fără șirul de interogare — adică fără `t1`/`t2`.
 *
 * Serverul îi taie oricum la salvare, dar câmpul din ecran se resincroniza doar când valoarea
 * întoarsă de server SE SCHIMBA. La a doua lipire a aceluiași URL, valoarea tăiată era identică
 * cu cea din bază, nimic nu se schimba, efectul de sincronizare nu se declanșa, iar pe ecran
 * rămâneau tokenii tastați. Arăta ca și cum salvarea n-a funcționat, deși funcționase.
 *
 * Deci se taie și aici, imediat după salvare, fără să depindem de ce răspunde serverul.
 */
function faraTokeni(url: string): string {
  return url.split('?')[0]
}

export function SetariEaPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.ea:write')

  const [apiBaseUrl, setApiBaseUrl] = useState('')
  const [t1, setT1] = useState('')
  const [t2, setT2] = useState('')
  const [getDataT1, setGetDataT1] = useState('')
  const [getDataT2, setGetDataT2] = useState('')
  const [npBaseUrl, setNpBaseUrl] = useState('')
  const [npT1, setNpT1] = useState('')
  const [npT2, setNpT2] = useState('')
  const [glBaseUrl, setGlBaseUrl] = useState('')
  const [glT1, setGlT1] = useState('')
  const [glT2, setGlT2] = useState('')
  const [partBaseUrl, setPartBaseUrl] = useState('')
  const [partT1, setPartT1] = useState('')
  const [partT2, setPartT2] = useState('')
  const [doBaseUrl, setDoBaseUrl] = useState('')
  const [diBaseUrl, setDiBaseUrl] = useState('')
  const [diT1, setDiT1] = useState('')
  const [diT2, setDiT2] = useState('')
  const [idT1, setIdT1] = useState('')
  const [idT2, setIdT2] = useState('')
  const [geUrl, setGeUrl] = useState('')
  const [geT1, setGeT1] = useState('')
  const [geT2, setGeT2] = useState('')
  const [doT1, setDoT1] = useState('')
  const [doT2, setDoT2] = useState('')
  const [enabled, setEnabled] = useState(false)

  const query = useQuery<SettingsResp>({
    queryKey: ['ea-settings'],
    queryFn: async () => (await api.get<SettingsResp>('/ea/settings')).data,
    refetchInterval: 60_000,
  })

  const s = query.data?.settings
  const lastSync = s?.lastSyncAt ? new Date(s.lastSyncAt) : null

  // Pre-fill local state cand se incarca settings (apiBaseUrl + enabled).
  // Tokens raman goale ca user-ul sa stie ca sunt deja salvate.
  useEffect(() => {
    if (s) {
      if (s.apiBaseUrl) setApiBaseUrl(s.apiBaseUrl)
      if (s.newPaymentsBaseUrl) setNpBaseUrl(s.newPaymentsBaseUrl)
      if (s.newGlBaseUrl) setGlBaseUrl(s.newGlBaseUrl)
      if (s.newPartnerBaseUrl) setPartBaseUrl(s.newPartnerBaseUrl)
      if (s.docOutBaseUrl) setDoBaseUrl(s.docOutBaseUrl)
      if (s.docInBaseUrl) setDiBaseUrl(s.docInBaseUrl)
      setEnabled(s.enabled)
    }
  }, [
    s?.apiBaseUrl,
    s?.newPaymentsBaseUrl,
    s?.newGlBaseUrl,
    s?.newPartnerBaseUrl,
    s?.docOutBaseUrl,
    s?.enabled,
  ])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { enabled }
      if (apiBaseUrl.trim()) body.apiBaseUrl = apiBaseUrl.trim()
      if (t1.trim()) body.t1 = t1.trim()
      if (t2.trim()) body.t2 = t2.trim()
      if (getDataT1.trim()) body.getDataT1 = getDataT1.trim()
      if (getDataT2.trim()) body.getDataT2 = getDataT2.trim()
      if (npBaseUrl.trim()) body.newPaymentsBaseUrl = npBaseUrl.trim()
      if (npT1.trim()) body.newPaymentsT1 = npT1.trim()
      if (npT2.trim()) body.newPaymentsT2 = npT2.trim()
      if (glBaseUrl.trim()) body.newGlBaseUrl = glBaseUrl.trim()
      if (glT1.trim()) body.newGlT1 = glT1.trim()
      if (glT2.trim()) body.newGlT2 = glT2.trim()
      if (partBaseUrl.trim()) body.newPartnerBaseUrl = partBaseUrl.trim()
      if (partT1.trim()) body.newPartnerT1 = partT1.trim()
      if (partT2.trim()) body.newPartnerT2 = partT2.trim()
      if (doBaseUrl.trim()) body.docOutBaseUrl = doBaseUrl.trim()
      if (doT1.trim()) body.docOutT1 = doT1.trim()
      if (doT2.trim()) body.docOutT2 = doT2.trim()
      if (diBaseUrl.trim()) body.docInBaseUrl = diBaseUrl.trim()
      if (diT1.trim()) body.docInT1 = diT1.trim()
      if (diT2.trim()) body.docInT2 = diT2.trim()
      if (idT1.trim()) body.invDocsT1 = idT1.trim()
      if (idT2.trim()) body.invDocsT2 = idT2.trim()
      if (geUrl.trim()) body.glExportUrl = geUrl.trim()
      if (geT1.trim()) body.glExportT1 = geT1.trim()
      if (geT2.trim()) body.glExportT2 = geT2.trim()
      return (await api.put('/ea/settings', body)).data
    },
    onSuccess: () => {
      toast.success('Setările EA au fost salvate')
      queryClient.invalidateQueries({ queryKey: ['ea-settings'] })
      // Tokenii dispar din câmpuri chiar acum. Nu se așteaptă răspunsul serverului: dacă URL-ul
      // tăiat iese identic cu cel din bază, refetch-ul nu aduce nicio schimbare și câmpul ar
      // rămâne cu tokenii pe ecran.
      setApiBaseUrl((v) => faraTokeni(v))
      setNpBaseUrl((v) => faraTokeni(v))
      setDoBaseUrl((v) => faraTokeni(v))
      setDiBaseUrl((v) => faraTokeni(v))
      setT1('')
      setT2('')
      setGetDataT1('')
      setGetDataT2('')
      setDoT1('')
      setDoT2('')
      setDiT1('')
      setDiT2('')
      setNpT1('')
      setGlT1('')
      setGlT2('')
      setNpT2('')
      setGeUrl('')
      setGeT1('')
      setGeT2('')
    },
    onError: showError,
  })

  /**
   * „Test conexiune, cum se stie ce api testeaza?" — nu se stia. Apela un singur `get_invdocs`
   * si spunea „Conexiune OK", desi EA are aici patru seturi de tokeni (plus altele configurate
   * din alte ecrane). Acum raspunde pe fiecare API in parte, iar rezultatul ramane pe ecran:
   * un toast care dispare in trei secunde nu e un raport.
   */
  const [teste, setTeste] = useState<RezultatTest[] | null>(null)
  const testMutation = useMutation({
    // Apelurile se fac in serie catre EA; pragul obisnuit de 15s e prea scurt.
    mutationFn: async () => (await api.post('/ea/test-connection', {}, { timeout: 90_000 })).data,
    onSuccess: (d: unknown) => {
      const r = d as { teste?: RezultatTest[] }
      setTeste(r.teste ?? [])
      const rele = (r.teste ?? []).filter((t) => t.stare === 'eroare').length
      if (rele > 0) toast.error(`${rele} din ${r.teste?.length ?? 0} API-uri nu răspund`)
      else toast.success('Testele au rulat — vezi rezultatul sub butoane')
    },
    onError: showError,
  })

  const [diagnostics, setDiagnostics] = useState<unknown>(null)
  const diagnosticsMutation = useMutation({
    mutationFn: async () => (await api.get('/ea/diagnostics')).data,
    onSuccess: (d) => {
      setDiagnostics(d)
      toast.success('Diagnostic generat — vezi mai jos')
    },
    onError: showError,
  })

  const [testFetch, setTestFetch] = useState<unknown>(null)
  const [testDate, setTestDate] = useState('2026-04-01')
  const testFetchMutation = useMutation({
    mutationFn: async () => (await api.get(`/ea/test-fetch?date=${testDate}`)).data,
    onSuccess: (d) => {
      setTestFetch(d)
      toast.success('Testul pe o zi a reușit — vezi rezultatul mai jos')
    },
    onError: showError,
  })

  const [syncTest, setSyncTest] = useState<unknown>(null)
  const syncTestMutation = useMutation({
    mutationFn: async () => (await api.post('/ea/sync-test?days=10')).data,
    onSuccess: (d) => {
      setSyncTest(d)
      toast.success('Testul de sincronizare rulează pe 10 zile — vezi raportul mai jos')
    },
    onError: showError,
  })

  const [syncStatus, setSyncStatus] = useState<unknown>(null)
  const syncStatusMutation = useMutation({
    mutationFn: async () => (await api.get('/ea/sync-status')).data,
    onSuccess: (d) => setSyncStatus(d),
    onError: showError,
  })

  const [partnersTest, setPartnersTest] = useState<unknown>(null)
  const [cifQuery, setCifQuery] = useState('')
  const partnersTestMutation = useMutation({
    mutationFn: async () => {
      const q = cifQuery.trim() ? `?cif=${encodeURIComponent(cifQuery.trim())}` : ''
      return (await api.get(`/ea/test-partners${q}`, { timeout: 90_000 })).data
    },
    onSuccess: (d) => {
      setPartnersTest(d)
      toast.success('Test parteneri rulat — vezi rezultatul mai jos')
    },
    onError: showError,
  })

  const syncMutation = useMutation({
    mutationFn: async (full: boolean) =>
      (await api.post(`/ea/sync-now${full ? '?full=1' : ''}`)).data,
    onSuccess: (d: unknown) => {
      const r = d as {
        totalFetched?: number
        upserted?: number
        mode?: 'sync' | 'queued'
        message?: string
      }
      if (r.mode === 'queued') {
        toast.success(r.message ?? 'Sincronizarea a pornit în fundal')
      } else {
        toast.success(
          `Sincronizare EA: ${r.upserted ?? 0} facturi actualizate din ${r.totalFetched ?? 0} citite`,
        )
      }
      queryClient.invalidateQueries({ queryKey: ['ea-settings'] })
    },
    onError: showError,
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → ExpertAccounts</h1>

      <Card title="Starea conexiunii">
        {s ? (
          <div className="space-y-2 text-sm">
            <div>
              <strong>Adresă API:</strong> <span className="font-mono">{s.apiBaseUrl ?? '—'}</span>
            </div>
            <div>
              <strong>Tokeni (t1 + t2):</strong>{' '}
              {s.tokensSet ? '✓ configurate' : '✗ nu sunt setate'}
            </div>
            <div>
              <strong>Activ:</strong> {s.enabled ? '✓ da' : '✗ nu'}
            </div>
            <div className="text-xs text-b4l-text-muted">
              Ultima sincronizare:{' '}
              {lastSync
                ? `${lastSync.toLocaleString('en-GB')} (${s.lastSyncCount ?? 0} facturi)`
                : 'niciodată'}
            </div>
          </div>
        ) : (
          <p className="text-sm text-b4l-text-muted">Nu există setări salvate.</p>
        )}
      </Card>

      <Card
        title="Configurare API EA"
        description="Lipește URL-ul pe care ți-l dă EA (EA → Setări → API). Conține deja t1 și t2 — se extrag automat, se criptează, iar din URL rămâne salvată doar adresa. Câmpurile t1/t2 de mai jos se completează doar dacă ai tokenii fără URL."
      >
        <div className="space-y-3">
          <Field
            label="Adresa API (URL)"
            placeholder="https://expertaccounts.exemplu.ro"
            value={apiBaseUrl}
            onChange={(e) => setApiBaseUrl(e.target.value)}
            disabled={!canWrite}
            hint="URL-ul instanței EA. Poți lipi și forma cu ?api=public&t1=…&t2=… — tokenii se extrag singuri."
          />
          <PasswordField
            label={
              s?.tokensSet ? 't1 nou (lasă gol pentru a păstra)' : 't1 (opțional, dacă e în URL)'
            }
            value={t1}
            onChange={(e) => setT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.tokensSet ? 't2 nou (lasă gol pentru a păstra)' : 't2 (opțional, dacă e în URL)'
            }
            value={t2}
            onChange={(e) => setT2(e.target.value)}
            disabled={!canWrite}
          />
          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Tokeni pentru API-ul EA „GET export data” (identificarea partenerilor din deconturi,
              prin CIF). În EA configurează un API separat de tip „GET export data” — primești o
              pereche t1/t2 proprie, diferită de cea pentru facturi.
              {s?.getDataTokensSet ? ' ✓ configurate' : ' ✗ nu sunt setate'}
            </p>
          </div>
          <PasswordField
            label={
              s?.getDataTokensSet
                ? 'GET export data — t1 nou (lasă gol pentru a păstra)'
                : 'GET export data — t1'
            }
            value={getDataT1}
            onChange={(e) => setGetDataT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.getDataTokensSet
                ? 'GET export data — t2 nou (lasă gol pentru a păstra)'
                : 'GET export data — t2'
            }
            value={getDataT2}
            onChange={(e) => setGetDataT2(e.target.value)}
            disabled={!canWrite}
          />
          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „API &lt; New journal entry” (<code>new_GL</code>) — scrie încasările
              rambursurilor pe factură, ca notă contabilă. Înlocuiește „New payments”: când e
              configurat, „Trimite în EA” de la Rambursuri curieri îl folosește pe el. Lipește
              URL-ul din EA (conține t1 și t2).
              {s?.newGlTokensSet ? ' ✓ configurate' : ' ✗ nu sunt setate'}
            </p>
          </div>
          <Field
            label="New journal entry — URL"
            placeholder="https://expertaccounts.exemplu.ro/?api=public&t1=…&t2=…"
            value={glBaseUrl}
            onChange={(e) => setGlBaseUrl(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.newGlTokensSet
                ? 'New journal entry — t1 nou (lasă gol pentru a păstra)'
                : 'New journal entry — t1 (opțional, dacă e în URL)'
            }
            value={glT1}
            onChange={(e) => setGlT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.newGlTokensSet
                ? 'New journal entry — t2 nou (lasă gol pentru a păstra)'
                : 'New journal entry — t2 (opțional, dacă e în URL)'
            }
            value={glT2}
            onChange={(e) => setGlT2(e.target.value)}
            disabled={!canWrite}
          />

          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „API New payments” (expirat 12.09.2026, înlocuit de New journal entry) — scrie
              nota de încasare pe factură (rambursurile colectate de curier). Are URL propriu și o
              pereche t1/t2 proprie.
              {s?.newPaymentsTokensSet ? ' ✓ configurate' : ' ✗ nu sunt setate'}
            </p>
          </div>
          <Field
            label="New payments — URL"
            placeholder="https://expertaccounts.exemplu.ro/..."
            value={npBaseUrl}
            onChange={(e) => setNpBaseUrl(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.newPaymentsTokensSet
                ? 'New payments — t1 nou (lasă gol pentru a păstra)'
                : 'New payments — t1 (opțional, dacă e în URL)'
            }
            value={npT1}
            onChange={(e) => setNpT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.newPaymentsTokensSet
                ? 'New payments — t2 nou (lasă gol pentru a păstra)'
                : 'New payments — t2 (opțional, dacă e în URL)'
            }
            value={npT2}
            onChange={(e) => setNpT2(e.target.value)}
            disabled={!canWrite}
          />

          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „API New partner” — clienții, furnizorii și punctele de lucru creați în CRM se
              trimit imediat în ExpertAccounts, la salvare. Fără el, un client nou ajunge în
              ExpertAccounts doar la prima comandă, iar un <strong>furnizor</strong> nu ajunge
              niciodată. La salvarea tokenilor, partenerii netrimiși încă se trimit automat.
              {s?.newPartnerTokensSet ? ' ✓ configurate' : ' ✗ nu sunt setate'}
            </p>
          </div>
          <Field
            label="New partner — URL"
            placeholder="https://expertaccounts.exemplu.ro/..."
            value={partBaseUrl}
            onChange={(e) => setPartBaseUrl(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.newPartnerTokensSet
                ? 'New partner — t1 nou (lasă gol pentru a păstra)'
                : 'New partner — t1 (opțional, dacă e în URL)'
            }
            value={partT1}
            onChange={(e) => setPartT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.newPartnerTokensSet
                ? 'New partner — t2 nou (lasă gol pentru a păstra)'
                : 'New partner — t2 (opțional, dacă e în URL)'
            }
            value={partT2}
            onChange={(e) => setPartT2(e.target.value)}
            disabled={!canWrite}
          />
          {s?.newPartnerTokensSet && <TestNewPartner />}

          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „new_docOUT_stock” — scrie <strong>bonul de consum</strong> pentru mostrele de
              sampling <strong>și transferurile</strong> către gestiunea de sampling. Aici se
              configurează amândouă: e același add-on. Singurul care MIȘCĂ STOC, de aceea are tokeni
              separați — se poate retrage fără să oprim citirile.
              {s?.docOutTokensSet ? ' ✓ configurate' : ' ✗ nu sunt setate'}
            </p>
          </div>
          <Field
            label="Bon de consum și transferuri (docOUT) — URL"
            placeholder="https://expertaccounts.exemplu.ro/..."
            value={doBaseUrl}
            onChange={(e) => setDoBaseUrl(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.docOutTokensSet
                ? 'Bon de consum — t1 nou (lasă gol pentru a păstra)'
                : 'Bon de consum — t1 (opțional, dacă e în URL)'
            }
            value={doT1}
            onChange={(e) => setDoT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.docOutTokensSet
                ? 'Bon de consum — t2 nou (lasă gol pentru a păstra)'
                : 'Bon de consum — t2 (opțional, dacă e în URL)'
            }
            value={doT2}
            onChange={(e) => setDoT2(e.target.value)}
            disabled={!canWrite}
          />

          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „API &lt; New inventory entry” (<code>new_docIN</code>) — oglinda celui de mai
              sus, pentru <strong>intrări în stoc</strong> și pentru <strong>inventarul</strong>
              (stock-take) care închide o acțiune de sampling. Nu servește la primirea
              transferurilor: aceea se face din EA. Cere un <strong>userid dedicat</strong> în EA,
              deci și tokeni proprii.
              {s?.docInTokensSet ? ' ✓ configurate' : ' ✗ nu sunt setate'}
            </p>
          </div>
          <Field
            label="Intrări în gestiune (docIN) — URL"
            placeholder="https://expertaccounts.exemplu.ro/..."
            value={diBaseUrl}
            onChange={(e) => setDiBaseUrl(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.docInTokensSet
                ? 'Intrări — t1 nou (lasă gol pentru a păstra)'
                : 'Intrări — t1 (opțional, dacă e în URL)'
            }
            value={diT1}
            onChange={(e) => setDiT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.docInTokensSet
                ? 'Intrări — t2 nou (lasă gol pentru a păstra)'
                : 'Intrări — t2 (opțional, dacă e în URL)'
            }
            value={diT2}
            onChange={(e) => setDiT2(e.target.value)}
            disabled={!canWrite}
          />

          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „API &lt; GET export data”, pentru <strong>lotul</strong> de pe documentul de
              ieșire — ca primirea transferului să intre în lotul care a plecat. Fără el lotul îl
              alege EA după regula lui: <code>get_invdocs</code> nu întoarce <code>batch</code> sau{' '}
              <code>lotid</code>, iar <code>infolot</code> vine gol.
              <br />
              <strong>Sursa nu e încă știută.</strong> <code>sqlStocMvmt()</code> și{' '}
              <code>sqlStocDoc()</code> răspund amândouă cu <code>select * from s</code> — relația{' '}
              <code>s</code> nu există — deși <code>sqlItemsMaster()</code> merge. Deci funcțiile de
              stoc par indisponibile aici; de confirmat cu ExpertAccounts.
              <br />
              În fiecare câmp intră <em>doar</em> valoarea, fără <code>Var1 =</code> în față. Var2:{' '}
              <code>{'{"filename":"","date_format":"d-M-Y","format":"json"}'}</code>
              {s?.invDocsTokensSet ? ' ✓ tokeni configurați' : ' ✗ tokenii nu sunt setați'}
            </p>
          </div>
          <PasswordField
            label={
              s?.invDocsTokensSet
                ? 'Mișcări de stoc — t1 nou (lasă gol pentru a păstra)'
                : 'Mișcări de stoc — t1'
            }
            value={idT1}
            onChange={(e) => setIdT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.invDocsTokensSet
                ? 'Mișcări de stoc — t2 nou (lasă gol pentru a păstra)'
                : 'Mișcări de stoc — t2'
            }
            value={idT2}
            onChange={(e) => setIdT2(e.target.value)}
            disabled={!canWrite}
          />

          <div className="mt-2 border-t border-b4l-border pt-2">
            <p className="mb-1 text-xs font-medium">Export note contabile (crm_b4l_gl)</p>
            <p className="mb-1 text-xs text-b4l-text-muted">
              Add-on „API &lt; GET export data” cu <code>Var1 = gl</code>. CRM-ul copiază zilnic, la
              05:50, notele contabile ale lunii curente și ale celei precedente; din copie se
              calculează estimarea TVA din <strong>Bani → Taxe &amp; impozite</strong>. Var2:{' '}
              <code>{'{"filename":"","format":"json","date_format":"Y-m-d"}'}</code>
              {s?.glExportTokensSet ? ' ✓ tokeni configurați' : ' ✗ tokenii nu sunt setați'}
            </p>
          </div>
          <PasswordField
            label={
              s?.glExportTokensSet
                ? 'Export note contabile — Endpoint URL nou din EA (lasă gol pentru a păstra)'
                : 'Export note contabile — Endpoint URL din EA (t1 și t2 se iau din el)'
            }
            value={geUrl}
            onChange={(e) => setGeUrl(e.target.value)}
            disabled={!canWrite}
          />
          <p className="-mt-1 text-xs text-b4l-text-muted">
            Sau, dacă ai tokenii separat, completează t1 și t2 mai jos.
          </p>
          <PasswordField
            label={
              s?.glExportTokensSet
                ? 'Export note contabile — t1 nou (lasă gol pentru a păstra)'
                : 'Export note contabile — t1'
            }
            value={geT1}
            onChange={(e) => setGeT1(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              s?.glExportTokensSet
                ? 'Export note contabile — t2 nou (lasă gol pentru a păstra)'
                : 'Export note contabile — t2'
            }
            value={geT2}
            onChange={(e) => setGeT2(e.target.value)}
            disabled={!canWrite}
          />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={!canWrite}
            />
            <span>Activ (sincronizare automată la 4 ore + reconciliere zilnică la 08:00)</span>
          </label>

          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <>
                <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
                  Salvează
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  loading={testMutation.isPending}
                  disabled={!s?.tokensSet}
                >
                  Testează toate API-urile
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => syncMutation.mutate(false)}
                  loading={syncMutation.isPending}
                  disabled={!s?.tokensSet || !s?.enabled}
                >
                  Sincronizează modificările
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (
                      confirm(
                        'Sincronizarea completă recitește toate facturile din ultimele 90 de zile și poate dura câteva minute. Nu pierzi nimic din CRM. Continui?',
                      )
                    )
                      syncMutation.mutate(true)
                  }}
                  loading={syncMutation.isPending}
                  disabled={!s?.tokensSet || !s?.enabled}
                >
                  Sincronizează 90 de zile
                </Button>
              </>
            )}
          </div>

          {teste && (
            <div className="mt-3 divide-y divide-b4l-border rounded-lg border border-b4l-border">
              {teste.map((t) => (
                <div key={t.nume} className="flex items-start gap-3 p-2.5 text-sm">
                  <span
                    className={`mt-0.5 rounded-full px-2 py-0.5 text-xs ${
                      t.stare === 'ok'
                        ? 'bg-b4l-green-light text-b4l-green-dark'
                        : t.stare === 'eroare'
                          ? 'bg-b4l-red-light text-b4l-red-dark'
                          : 'bg-b4l-bg text-b4l-text-muted'
                    }`}
                  >
                    {t.stare === 'ok'
                      ? 'merge'
                      : t.stare === 'eroare'
                        ? 'nu merge'
                        : t.stare === 'raspuns'
                          ? 'răspunde'
                          : 'neconfigurat'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-b4l-text">{t.nume}</div>
                    <div className="text-xs text-b4l-text-muted">{t.foloseste}</div>
                    <div className="mt-0.5 break-words font-mono text-xs text-b4l-text-dim">
                      {t.detaliu}
                    </div>
                  </div>
                </div>
              ))}
              <p className="p-2.5 text-xs text-b4l-text-muted">
                Cele care <strong>scriu</strong> în ExpertAccounts nu se testează cu un document
                real — ar lăsa în urmă o notă de încasare sau un bon de consum. Li se trimite un
                mesaj gol, din care EA nu are ce crea: dacă răspunde plângându-se de conținut,
                tokenii și adresa sunt bune.
              </p>
            </div>
          )}
        </div>
      </Card>

      <Card
        title="Starea sincronizării"
        description="Totalul facturilor din CRM și ultimele 5 sincronizate. Apasă „Reîmprospătează” în timpul sincronizării ca să vezi progresul."
      >
        <Button
          variant="secondary"
          onClick={() => syncStatusMutation.mutate()}
          loading={syncStatusMutation.isPending}
        >
          Reîmprospătează
        </Button>
        {syncStatus ? (
          <pre className="mt-3 max-h-96 overflow-auto rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-[11px]">
            {JSON.stringify(syncStatus, null, 2)}
          </pre>
        ) : null}
      </Card>

      <Card
        title="Test parteneri (GET export data)"
        description="Caută coduri fiscale în lista de parteneri din EA. Lasă gol ca să vezi doar numărul total de parteneri și câteva exemple."
      >
        <div className="flex items-end gap-2">
          <Field
            label="CIF-uri de verificat (separate prin virgulă)"
            value={cifQuery}
            onChange={(e) => setCifQuery(e.target.value)}
            placeholder="41919630, RO17233051, 10071440"
          />
          <Button
            variant="secondary"
            onClick={() => partnersTestMutation.mutate()}
            loading={partnersTestMutation.isPending}
          >
            Test parteneri
          </Button>
        </div>
        {partnersTest ? (
          <pre className="mt-3 max-h-96 overflow-auto rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-[11px]">
            {JSON.stringify(partnersTest, null, 2)}
          </pre>
        ) : null}
      </Card>

      <Card
        title="Diagnostic"
        description="Distribuția facturilor din CRM pe tip document / serie / lună — utilă ca să verifici dacă sincronizarea a adus toate facturile."
      >
        <Button
          variant="secondary"
          onClick={() => diagnosticsMutation.mutate()}
          loading={diagnosticsMutation.isPending}
        >
          Generează diagnosticul
        </Button>
        {diagnostics ? (
          <pre className="mt-3 max-h-96 overflow-auto rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-[11px]">
            {JSON.stringify(diagnostics, null, 2)}
          </pre>
        ) : null}
      </Card>

      <Card
        title="Test de citire pe o zi"
        description="Apelează EA pentru o singură zi, cu exact adresa folosită de sincronizare. Compară cu ce primești manual din browser."
      >
        <div className="flex items-end gap-2">
          <Field
            label="Data (YYYY-MM-DD)"
            value={testDate}
            onChange={(e) => setTestDate(e.target.value)}
          />
          <Button
            variant="secondary"
            onClick={() => testFetchMutation.mutate()}
            loading={testFetchMutation.isPending}
          >
            Testează ziua
          </Button>
        </div>
        {testFetch ? (
          <pre className="mt-3 max-h-96 overflow-auto rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-[11px]">
            {JSON.stringify(testFetch, null, 2)}
          </pre>
        ) : null}
      </Card>

      <Card
        title="Test de sincronizare (ultimele 10 zile)"
        description="Apelează EA pe rând pentru fiecare din ultimele 10 zile și raportează pe zi (înregistrări, facturi reținute, documente unice, timp de răspuns). Util ca să vedem dacă apar limitări sau erori la apeluri rapide consecutive."
      >
        <Button
          variant="secondary"
          onClick={() => syncTestMutation.mutate()}
          loading={syncTestMutation.isPending}
        >
          Rulează testul pe 10 zile
        </Button>
        {syncTest ? (
          <pre className="mt-3 max-h-96 overflow-auto rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-[11px]">
            {JSON.stringify(syncTest, null, 2)}
          </pre>
        ) : null}
      </Card>

      <EaModuleLicensesCard />

      <DocumenteBruteEa />

      <Card description="Despre: EA e sursa fiscală (declarațiile vin din EA). Verificarea de conformitate compară facturile EA (FB = Shopify, DSMF = eMAG, SRT = farmacii) cu e-Factura ANAF și raportează diferențele.">
        <p className="text-xs text-b4l-text-muted">
          <strong>Notă:</strong> verificarea rulează zilnic la 08:00 pentru luna curentă și cea
          precedentă. Vezi <code>Bani → Reconciliere SPV ↔ EA</code>.
        </p>
      </Card>
    </div>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}

interface ModuleLicense {
  name: string
  status: 'activa' | 'expirata' | 'trial_expirat'
  validUntil: string | null
  expiredSince: string | null
  pricePerYear: number | null
  ackedFor: string | null
  ackedByName: string | null
  notNeeded: boolean
  notNeededByName: string | null
  notNeededAt: string | null
  /** Ce face CRM-ul cu modulul; la cele folosite bifa „Nu e necesar" nu se poate pune. */
  folositPentru: string | null
  syncedAt: string
}

const zi = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—')

/**
 * Licentele modulelor EA. EA nu le expune prin API si notificarile lor pe email nu ajung,
 * asa ca starea se aduce lipind textul paginii „Configurari → Module suplimentare". O
 * actualizare tine un an: datele de expirare nu se schimba pana la reinnoire.
 */
function EaModuleLicensesCard() {
  const queryClient = useQueryClient()
  /*
    Bifa „Nu e necesar" stinge avertismentul de expirare, deci o pot pune doar adminii.

    Serverul o cerea deja (`settings.ea:write` = doar admin), dar bifa se afisa activa si
    contabilitatii, care are drept de CITIRE pe pagina. Clicul ei primea 403, fara ca omul sa
    inteleaga de ce. Restul butoanelor din ecran erau deja dezactivate corect.
  */
  const poateScrie = useAuthStore((s) => s.permissions).includes('settings.ea:write')
  const [text, setText] = useState('')
  // Pragul de avertizare, setabil: cu cate zile inainte de expirare sa apara pop-up-ul.
  const [zile, setZile] = useState<string>('')

  const setariQ = useQuery<{ settings: { licenseAlertDays?: number } | null }>({
    queryKey: ['ea', 'settings', 'license-days'],
    queryFn: async () => (await api.get('/ea/settings')).data,
  })
  useEffect(() => {
    const z = setariQ.data?.settings?.licenseAlertDays
    if (z != null && zile === '') setZile(String(z))
  }, [setariQ.data, zile])

  const zileMut = useMutation({
    mutationFn: async () =>
      (await api.put('/ea/settings', { licenseAlertDays: Number(zile) })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea', 'settings'] })
      queryClient.invalidateQueries({ queryKey: ['ea', 'module-licenses'] })
      toast.success(`Avertisment cu ${zile} zile înainte de expirare`)
    },
    onError: showError,
  })

  const q = useQuery<{ modules: ModuleLicense[] }>({
    queryKey: ['ea', 'module-licenses', 'list'],
    queryFn: async () => (await api.get('/ea/module-licenses')).data,
  })

  const pasteMut = useMutation({
    mutationFn: async () => (await api.post('/ea/module-licenses/paste', { text })).data,
    onSuccess: (d: { saved: number; removed: number }) => {
      queryClient.invalidateQueries({ queryKey: ['ea', 'module-licenses'] })
      setText('')
      toast.success(
        d.removed > 0
          ? `${d.saved} module actualizate, ${d.removed} eliminate din EA`
          : `${d.saved} module actualizate`,
      )
    },
    onError: showError,
  })

  const nuMaiTrebuieMut = useMutation({
    mutationFn: async (v: { name: string; value: boolean }) =>
      (await api.post('/ea/module-licenses/not-needed', v)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ea', 'module-licenses'] }),
    onError: showError,
  })

  const modules = q.data?.modules ?? []
  const azi = new Date().toISOString().slice(0, 10)
  const stare = (m: ModuleLicense) => {
    if (m.status === 'trial_expirat') return { text: 'Trial expirat', cls: 'text-slate-400' }
    if (m.status === 'expirata') return { text: 'EXPIRAT', cls: 'text-red-700 font-semibold' }
    if (m.validUntil && m.validUntil <= azi) return { text: 'EXPIRAT', cls: 'text-red-700' }
    return { text: 'Activă', cls: 'text-green-700' }
  }

  return (
    <Card
      title="Licențe module ExpertAccounts"
      description="EA nu expune licențele prin API, iar notificările lor pe email nu ajung. Copiază toată pagina „Configurări → Module suplimentare” din EA și lipește-o aici. Cu 7 zile înainte de expirare, adminii primesc un avertisment la intrarea în CRM."
    >
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-b4l-text-muted">
          Avertizează cu
          <input
            type="number"
            min={1}
            max={365}
            value={zile}
            onChange={(e) => setZile(e.target.value)}
            className="mx-2 w-20 rounded border border-b4l-border px-2 py-1 text-sm"
          />
          zile înainte de expirare
        </label>
        <Button
          variant="secondary"
          onClick={() => zileMut.mutate()}
          loading={zileMut.isPending}
          disabled={!zile || Number(zile) < 1}
        >
          Salvează pragul
        </Button>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Lipește aici textul paginii „Module suplimentare” din ExpertAccounts…"
        className="w-full rounded border border-b4l-border p-2 text-xs"
      />
      <div className="mt-2 flex items-center gap-2">
        <Button
          onClick={() => pasteMut.mutate()}
          loading={pasteMut.isPending}
          disabled={!text.trim()}
        >
          Actualizează licențele
        </Button>
        {modules.length > 0 && (
          <span className="text-xs text-b4l-text-muted">
            {modules.length} module · actualizat {zi(modules[0].syncedAt)}
          </span>
        )}
      </div>

      {modules.length > 0 && (
        <div className="mt-3 max-h-80 overflow-auto rounded border border-b4l-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-b4l-bg text-left text-b4l-text-muted">
              <tr>
                <th className="p-1.5">Modul</th>
                <th className="p-1.5">Stare</th>
                <th className="p-1.5">Până la</th>
                <th className="p-1.5 text-right">RON/an</th>
                <th className="p-1.5">Confirmat</th>
                <th className="p-1.5 text-center">Nu e necesar</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => {
                const s = stare(m)
                return (
                  <tr key={m.name} className="border-t border-b4l-border">
                    <td className="p-1.5">{m.name}</td>
                    <td className={`p-1.5 ${s.cls}`}>{s.text}</td>
                    <td className="p-1.5">{zi(m.validUntil ?? m.expiredSince)}</td>
                    <td className="p-1.5 text-right">{m.pricePerYear ?? '—'}</td>
                    <td className="p-1.5 text-b4l-text-muted">
                      {m.ackedFor ? `${m.ackedByName ?? 'da'} (${zi(m.ackedFor)})` : '—'}
                    </td>
                    <td className="p-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={m.notNeeded}
                        /*
                          La un modul folosit de CRM bifa NU se poate pune — serverul o refuză. Se
                          poate doar scoate, pentru bifele puse înainte de regula asta.
                        */
                        disabled={!poateScrie || (m.folositPentru != null && !m.notNeeded)}
                        onChange={(e) =>
                          nuMaiTrebuieMut.mutate({ name: m.name, value: e.target.checked })
                        }
                        title={
                          m.folositPentru
                            ? `CRM-ul îl folosește pentru ${m.folositPentru} — avertismentul nu se poate stinge`
                            : poateScrie
                              ? 'Bifat: modulul rămâne în EA, dar nu mai declanșează avertismentul'
                              : 'Doar administratorii pot stinge avertismentul de expirare'
                        }
                      />
                      {/*
                        Cine a bifat, sub bifă. Bifa opreste avertismentul de expirare, deci e o
                        decizie cu urmari: „API < GET items stock feed" a expirat asa, necontrolat,
                        iar stocul din CRM a stat inghetat cinci zile.
                      */}
                      {m.notNeeded && (
                        <div className="mt-0.5 text-[10px] leading-tight text-b4l-text-muted">
                          {m.notNeededByName ?? 'autor necunoscut'}
                          {m.notNeededAt ? ` · ${zi(m.notNeededAt)}` : ''}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

/**
 * Testul `new_partner` (etapa a, plan parteneri comuni): trimite în EA un singur partener de test,
 * fix, și arată cererea și răspunsul EA nemodificate. Doar admin.
 */
function TestNewPartner() {
  const isAdmin = useAuthStore((st) => st.user?.role === 'admin')
  const [rez, setRez] = useState<{
    configurat: boolean
    url: string
    cerere: unknown
    status: number | null
    raspuns: string
  } | null>(null)
  const trimite = useMutation({
    mutationFn: async () => (await api.post('/ea/new-partner/test', {}, { timeout: 60_000 })).data,
    onSuccess: (r) => setRez(r),
    onError: () => toast.error('Testul n-a putut fi trimis'),
  })
  if (!isAdmin) return null
  return (
    <div className="mt-2 space-y-2 rounded border border-b4l-border p-2 text-xs">
      <p className="text-b4l-text-muted">
        Trimite în ExpertAccounts partenerul de test „TEST PARTENER CRM 15.10.2026” (persoană
        fizică, Tel.0700000001, București Sector 1) cu punctul de lucru „Punct test”. Nu scrie nimic
        în CRM. După verificare, partenerul se șterge din ExpertAccounts.
      </p>
      <Button
        variant="secondary"
        disabled={trimite.isPending}
        onClick={() => trimite.mutate()}
        className="!text-xs"
      >
        {trimite.isPending ? 'Se trimite…' : 'Trimite partenerul de test'}
      </Button>
      {rez && (
        <div className="space-y-1">
          <div>
            <strong>Răspuns EA</strong> {rez.status != null ? `(HTTP ${rez.status})` : ''}:
          </div>
          <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-b4l-bg p-2">
            {rez.raspuns || '(gol)'}
          </pre>
          <details>
            <summary className="cursor-pointer text-b4l-text-muted">Cererea trimisă</summary>
            <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-b4l-bg p-2">
              {rez.url}
              {`
`}
              {JSON.stringify(rez.cerere, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}
